/* eslint-disable simple-import-sort/imports */
import { Navigate, useRoutes } from 'react-router-dom'

import AuthLayout from '@/layouts/auth'
import DashboardLayout from '@/layouts/default'
import { ProtectedRoute, BillingGuard, RouteGuard } from '@qse/config'
import { QSE_ENTERPRISE_ID } from '@qse/constants'
import {
  LoginScreen,
  RegisterScreen,
  CompleteRegistrationPage,
  ConfirmEmailPage,
  NewPasswordScreen,
  ForgotPasswordScreen,
  ResetPasswordScreen,
  TwoFAVerifyScreen,
  VerificationScreen,
  Setup2FAScreen,
  ProfileScreen,
  CheckInvitation,
} from '@qse/auth-ui'
import { UsersManagement } from '@/pages/management'
import { AppRoutesList } from './routes'
import Billing from '@/pages/billing/billing'
import Success from '@/pages/billing/success'
import Error from '@/pages/billing/error'
import SubscriptionManagement from '@/pages/management/subscription-managment'
import { UserRoleEnum } from '@qse/constants'
import { useUserStore } from '@qse/stores'

/** Tenant-Admin portal — TENANT_ADMIN routes only (client infra image). */
function AppRoutes() {
  const { user } = useUserStore()

  const routes = useRoutes([
    {
      path: '/',
      element: <Navigate replace to={AppRoutesList.login} />,
    },
    {
      element: (
        <ProtectedRoute type="public">
          <AuthLayout />
        </ProtectedRoute>
      ),
      children: [
        { path: AppRoutesList.login, element: <LoginScreen /> },
        { path: AppRoutesList.register, element: <RegisterScreen /> },
        { path: AppRoutesList.completeRegistration, element: <CompleteRegistrationPage /> },
        { path: AppRoutesList.confirmEmail, element: <ConfirmEmailPage /> },
        { path: AppRoutesList.newPassword, element: <NewPasswordScreen /> },
        { path: AppRoutesList.forgotPassword, element: <ForgotPasswordScreen /> },
        { path: AppRoutesList.resetPassword, element: <ResetPasswordScreen /> },
        { path: AppRoutesList.verification, element: <VerificationScreen /> },
        { path: AppRoutesList.twoFA, element: <Setup2FAScreen /> },
        { path: AppRoutesList.twoFAVerify, element: <TwoFAVerifyScreen /> },
      ],
    },
    {
      path: AppRoutesList.success,
      element: (
        <ProtectedRoute type="protected">
          <BillingGuard>
            <Success />
          </BillingGuard>
        </ProtectedRoute>
      ),
    },
    {
      path: AppRoutesList.error,
      element: <Error />,
    },
    {
      path: AppRoutesList.checkInvitation,
      element: <CheckInvitation />,
    },
    {
      element: (
        <ProtectedRoute type="protected">
          <BillingGuard>
            <DashboardLayout />
          </BillingGuard>
        </ProtectedRoute>
      ),
      children: [
        ...(import.meta.env.VITE_DEPLOYMENT_MODE !== 'onprem' &&
        user?.tenant?.resellerId !== QSE_ENTERPRISE_ID
          ? [
              {
                path: AppRoutesList.managementSubscription,
                element: <SubscriptionManagement />,
              },
            ]
          : []),
        {
          path: AppRoutesList.billing,
          element: <Billing />,
        },
        {
          path: AppRoutesList.profile,
          element: <ProfileScreen />,
        },
        {
          path: AppRoutesList.users,
          element: (
            <RouteGuard allowedRoles={[UserRoleEnum.TENANT_ADMIN]}>
              <UsersManagement />
            </RouteGuard>
          ),
        },
        {
          path: '*',
          element: (
            <Navigate
              replace
              state={{ showNotFoundError: true }}
              to={AppRoutesList.users}
            />
          ),
        },
      ],
    },
  ])

  return routes
}

export default AppRoutes
